import { assetName } from "@/components/assets/asset-presenter";
import type { AssetTypeFilter } from "./asset-explorer-preferences";
import type { Asset, AssetFolder } from "./queries";

export type AssetSmartView =
  | "untagged"
  | "missing-alt"
  | "missing-title"
  | "near-limit"
  | "recently-replaced"
  | null;
export type AssetSort =
  | "newest"
  | "oldest"
  | "name-asc"
  | "name-desc"
  | "largest"
  | "smallest";
export const ASSET_DRAG_MIME = "application/x-personalizehq-asset";
export const FOLDER_DRAG_MIME = "application/x-personalizehq-asset-folder";
export interface AssetDragPayload {
  kind: "asset";
  appId: string;
  ids: string[];
}
export interface FolderDragPayload {
  kind: "folder";
  appId: string;
  id: string;
}

export function parseAssetDrag(
  value: string,
  appId: string
): AssetDragPayload | null {
  try {
    const parsed = JSON.parse(value) as Partial<AssetDragPayload>;
    return parsed.kind === "asset" &&
      parsed.appId === appId &&
      Array.isArray(parsed.ids) &&
      parsed.ids.length > 0 &&
      parsed.ids.every((id) => typeof id === "string")
      ? (parsed as AssetDragPayload)
      : null;
  } catch {
    return null;
  }
}
export function parseFolderDrag(
  value: string,
  appId: string
): FolderDragPayload | null {
  try {
    const parsed = JSON.parse(value) as Partial<FolderDragPayload>;
    return parsed.kind === "folder" &&
      parsed.appId === appId &&
      typeof parsed.id === "string"
      ? (parsed as FolderDragPayload)
      : null;
  } catch {
    return null;
  }
}
export interface AssetFilters {
  query: string;
  type: AssetTypeFilter;
  tag: string | null;
  days: 0 | 7 | 30 | 90;
  sort: AssetSort;
  folderId: string | null;
  smart: AssetSmartView;
}

export function filterAssets(
  assets: Asset[],
  filters: AssetFilters,
  now = new Date()
): Asset[] {
  const needle = filters.query.trim().toLowerCase();
  const cutoff = filters.days ? now.getTime() - filters.days * 86_400_000 : 0;
  const recentReplacement = now.getTime() - 30 * 86_400_000;
  return assets
    .filter((asset) => {
      const image = asset.content_type.startsWith("image/");
      if (filters.folderId !== null && asset.folder_id !== filters.folderId)
        return false;
      if (filters.type === "images" && !image) return false;
      if (filters.type === "fonts" && image) return false;
      if (filters.tag && !asset.tags.includes(filters.tag)) return false;
      if (cutoff && new Date(asset.created_at).getTime() < cutoff) return false;
      const haystack = [
        assetName(asset),
        asset.title,
        asset.caption,
        asset.alt_text,
        asset.tags.join(" "),
        asset.content_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (needle && !haystack.includes(needle)) return false;
      if (filters.smart === "untagged" && asset.tags.length) return false;
      if (filters.smart === "missing-alt" && (!image || asset.alt_text?.trim()))
        return false;
      if (filters.smart === "missing-title" && (!image || asset.title?.trim()))
        return false;
      if (
        filters.smart === "near-limit" &&
        (asset.byte_size === null || asset.byte_size < 450 * 1024)
      )
        return false;
      if (
        filters.smart === "recently-replaced" &&
        (!asset.replaced_at ||
          new Date(asset.replaced_at).getTime() < recentReplacement)
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      if (filters.sort === "newest")
        return Date.parse(b.created_at) - Date.parse(a.created_at);
      if (filters.sort === "oldest")
        return Date.parse(a.created_at) - Date.parse(b.created_at);
      if (filters.sort === "largest")
        return (b.byte_size ?? -1) - (a.byte_size ?? -1);
      if (filters.sort === "smallest")
        return (
          (a.byte_size ?? Number.MAX_SAFE_INTEGER) -
          (b.byte_size ?? Number.MAX_SAFE_INTEGER)
        );
      const order = assetName(a).localeCompare(assetName(b), undefined, {
        numeric: true,
      });
      return filters.sort === "name-desc" ? -order : order;
    });
}

export interface FolderNode extends AssetFolder {
  children: FolderNode[];
}
export function buildFolderTree(folders: AssetFolder[]): {
  roots: FolderNode[];
  orphans: FolderNode[];
} {
  const nodes = new Map(
    folders.map((folder) => [
      folder.id,
      { ...folder, children: [] as FolderNode[] },
    ])
  );
  const roots: FolderNode[] = [];
  const orphans: FolderNode[] = [];
  for (const node of nodes.values()) {
    if (!node.parent_id) roots.push(node);
    else {
      const parent = nodes.get(node.parent_id);
      if (parent) parent.children.push(node);
      else orphans.push(node);
    }
  }
  const sort = (items: FolderNode[]) => {
    items.sort((a, b) => a.name.localeCompare(b.name));
    items.forEach((item) => sort(item.children));
  };
  sort(roots);
  sort(orphans);
  return { roots, orphans };
}

export function isFolderDropAllowed(
  folders: AssetFolder[],
  folderId: string,
  parentId: string | null
): boolean {
  const folder = folders.find((item) => item.id === folderId);
  if (!folder || folder.parent_id === parentId || folderId === parentId)
    return false;
  let cursor = parentId;
  while (cursor) {
    if (cursor === folderId) return false;
    cursor = folders.find((item) => item.id === cursor)?.parent_id ?? null;
  }
  return true;
}

export function nextSelection(
  visibleIds: string[],
  selected: Set<string>,
  id: string,
  anchor: string | null,
  modifiers: { toggle: boolean; range: boolean }
): { selected: Set<string>; anchor: string } {
  if (modifiers.range && anchor && visibleIds.includes(anchor)) {
    const start = visibleIds.indexOf(anchor);
    const end = visibleIds.indexOf(id);
    return {
      selected: new Set(
        visibleIds.slice(Math.min(start, end), Math.max(start, end) + 1)
      ),
      anchor,
    };
  }
  if (modifiers.toggle) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return { selected: next, anchor: id };
  }
  return { selected: new Set([id]), anchor: id };
}
